import { MessageOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import {
    Button,
    Card,
    Col,
    Form,
    Input,
    message,
    Modal,
    Row,
    Select,
    Space,
    Typography
} from 'antd';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

const { Title, Text } = Typography;
const { Option } = Select;

const SendCandidateWhatsAppModal = ({
  visible,
  onCancel,
  candidate,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const safeSelectFilter = (input, option) => {
    const searchInput = String(input || '').toLowerCase();
    const optionLabel = String(option?.label ?? option?.children ?? '').toLowerCase();
    return optionLabel.includes(searchInput);
  };

  const normalizeJobs = (payload) => {
    const rawJobs = Array.isArray(payload?.job_descriptions)
      ? payload.job_descriptions
      : (Array.isArray(payload?.jobs) ? payload.jobs : []);

    return rawJobs.map((job) => ({
      id: job?.id || '',
      title: job?.title || 'Untitled Role',
      company: job?.company || 'Unknown company',
      description: job?.description || '',
      location: job?.location || '',
      status: job?.status || 'active'
    })).filter((job) => !!job.id);
  };

  useEffect(() => {
    if (visible && candidate) {
      fetchJobs();
      // Pre-fill form with candidate data
      const phoneNumbers = Array.isArray(candidate.phoneNumbers)
        ? candidate.phoneNumbers
        : (candidate.phoneNumbers ? [candidate.phoneNumbers] : []);

      form.setFieldsValue({
        candidate_name: candidate.contactName,
        phone_number: phoneNumbers.length > 0 ? phoneNumbers[0] : ''
      });
    }
  }, [visible, candidate, form]);

  const fetchJobs = async () => {
    try {
      setJobsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/job_descriptions?limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        const normalizedJobs = normalizeJobs(response.data);
        setJobs(normalizedJobs);
        console.log('[SendCandidateWhatsAppModal] fetched jobs:', {
          count: normalizedJobs.length,
          preview: normalizedJobs.slice(0, 3).map((j) => ({ id: j.id, title: j.title }))
        });
        if (normalizedJobs.length === 0) {
          message.info('No jobs found for your account yet. Please create or upload a job first.');
        }
      } else {
        message.error('Failed to fetch jobs');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      message.error('Failed to fetch jobs');
    } finally {
      setJobsLoading(false);
    }
  };



  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const payload = {
        candidate_id: candidate.id,
        to_phone: values.phone_number,
        job_id: values.job_id
      };

      const response = await axios.post('/api/candidates/send-whatsapp', payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        message.success('WhatsApp message sent successfully!');
        onSuccess();
        onCancel();
        form.resetFields();
      } else {
        message.error(response.data.error || 'Failed to send WhatsApp message');
      }
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      message.error(error.response?.data?.error || 'Failed to send WhatsApp message');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          <MessageOutlined style={{ color: '#25D366' }} />
          <span>Send WhatsApp Message</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* Candidate Info Card */}
        {candidate && (
          <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f8f9fa' }}>
            <Row gutter={16} align="middle">
              <Col>
                <UserOutlined style={{ fontSize: 24, color: '#1890ff' }} />
              </Col>
              <Col flex="1">
                <Title level={5} style={{ margin: 0 }}>
                  {candidate.contactName}
                </Title>
                <Text type="secondary">
                  {candidate.job_title || candidate.title} at {candidate.company}
                </Text>
              </Col>
            </Row>
          </Card>
        )}

        {/* Job Selection */}
        <Form.Item
          label="Select Job"
          name="job_id"
          rules={[{ required: true, message: 'Please select a job' }]}
        >
          <Select
            placeholder="Choose a job to reference in the message"
            loading={jobsLoading}
            showSearch
            filterOption={safeSelectFilter}
            optionFilterProp="label"
          >
            {jobs && jobs.length > 0 ? jobs.map(job => (
              <Option key={job.id} value={job.id} label={`${job.title} - ${job.company}`}>
                {job.title} - {job.company}
              </Option>
            )) : null}
          </Select>
        </Form.Item>

        {/* Phone Number */}
        <Form.Item
          label={
            <Space>
              <PhoneOutlined />
              <span>Phone Number</span>
            </Space>
          }
          name="phone_number"
          rules={[
            { required: true, message: 'Please enter a phone number' },
            {
              pattern: /^\+?[1-9]\d{1,14}$/,
              message: 'Please enter a valid phone number (e.g., +6581219617)'
            }
          ]}
          help="Enter the phone number in international format (e.g., +6581219617)"
        >
          <Input placeholder="+6581219617" />
        </Form.Item>

        {/* Template Message Preview */}
        <Form.Item
          label={
            <Space>
              <MessageOutlined />
              <span>Template Message Preview</span>
            </Space>
          }
        >
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #d9d9d9'
          }}>
            <Text type="secondary" style={{ fontSize: '12px', marginBottom: '8px', display: 'block' }}>
              WhatsApp requires template messages for initial outreach. This message will be sent using the approved template:
            </Text>
            <div style={{
              backgroundColor: 'white',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #e8e8e8',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              <Text strong>Template Variables:</Text>
              <div style={{ marginTop: '8px' }}>
                • <Text code>{'{1}'}</Text> = Candidate's first name
              </div>
              <div>
                • <Text code>{'{2}'}</Text> = Job title (from selected job)
              </div>
              <div>
                • <Text code>{'{3}'}</Text> = Company name (from selected job)
              </div>
            </div>
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
              The actual message content will be automatically generated using the approved WhatsApp template.
            </Text>
          </div>
        </Form.Item>

        {/* Action Buttons */}
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<MessageOutlined />}
              style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
            >
              Send WhatsApp
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SendCandidateWhatsAppModal;
